import { RaynetError, RaynetResponse, RaynetLead, RaynetPerson, RaynetDropdownOption, RaynetCompany, RaynetContactPerson, AresCompany, CompanySearchResult, RaynetUser, RaynetCompanyCreateRequest, RaynetCompanyCreateResponse, RaynetAddressCreateRequest, RaynetAddressCreateResponse, RaynetContactPersonCreateRequest, RaynetContactPersonCreateResponse, RaynetMeetingCreateRequest, RaynetMeetingCreateResponse, SurveySubmissionRequest, SurveySubmissionResponse } from './types';

class RaynetClient {
  private baseUrl: string;
  private initialized = false;

  constructor() {
    // Use our backend API instead of calling Raynet directly
    this.baseUrl = import.meta.env.VITE_API_BASE_URL || 'http://localhost:3002/api';
  }

  private initialize() {
    if (this.initialized) return;
    this.initialized = true;
    console.log('Raynet klient inicializován pro backend:', this.baseUrl);
  }

  private async handleResponse<T>(response: Response): Promise<T> {
    const responseText = await response.text();
    
    if (!response.ok) {
      console.error('Backend API chyba:', response.status, responseText);
      
      let errorData;
      try {
        errorData = JSON.parse(responseText);
      } catch {
        errorData = { error: responseText };
      }
      
      if (response.status === 401) {
        throw new RaynetError('Neplatné přihlašovací údaje', 401, 'UNAUTHORIZED');
      }
      if (response.status === 403) {
        throw new RaynetError('Nedostatečná oprávnění', 403, 'FORBIDDEN');
      }
      if (response.status >= 500) {
        throw new RaynetError('Chyba serveru', response.status, 'SERVER_ERROR');
      }
      
      throw new RaynetError(errorData.error || 'Neočekávaná chyba API', response.status);
    }

    try {
      return JSON.parse(responseText);
    } catch (error) {
      console.error('Chyba parsování JSON:', error);
      throw new RaynetError('Neplatná odpověď ze serveru', response.status);
    }
  }

  async testConnection(): Promise<boolean> {
    try {
      this.initialize();

      const response = await fetch(`${this.baseUrl}/raynet/test`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        const data = await this.handleResponse<{ success: boolean }>(response);
        return data.success;
      }
      
      return false;
    } catch (error) {
      console.error('Test připojení selhal:', error);
      return false;
    }
  }

  async createLead(leadData: RaynetLead): Promise<RaynetResponse<RaynetLead>> {
    this.initialize();

    console.log('Vytváření lead přes backend:', leadData);

    const response = await fetch(`${this.baseUrl}/raynet/lead`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(leadData),
    });

    const result = await this.handleResponse<RaynetResponse<RaynetLead>>(response);
    console.log('Lead vytvořen:', result);
    
    return result;
  }

  async createPerson(personData: RaynetPerson): Promise<RaynetResponse<RaynetPerson>> {
    this.initialize();

    console.log('Vytváření osoby přes backend:', personData);

    const response = await fetch(`${this.baseUrl}/raynet/person`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(personData),
    });

    const result = await this.handleResponse<RaynetResponse<RaynetPerson>>(response);
    console.log('Osoba vytvořena:', result);
    
    return result;
  }

  async getContactSources(): Promise<RaynetResponse<RaynetDropdownOption[]>> {
    this.initialize();

    const response = await fetch(`${this.baseUrl}/raynet/contact-sources`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    });

    return this.handleResponse<RaynetResponse<RaynetDropdownOption[]>>(response);
  }

  async getLeadCategories(): Promise<RaynetResponse<RaynetDropdownOption[]>> {
    this.initialize();

    const response = await fetch(`${this.baseUrl}/raynet/lead-categories`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    });

    return this.handleResponse<RaynetResponse<RaynetDropdownOption[]>>(response);
  }

  async submitForm(formData: any): Promise<{ success: boolean; message: string; submissionId?: string }> {
    this.initialize();

    console.log('Odesílání formuláře přes backend:', formData);

    const response = await fetch(`${this.baseUrl}/submissions`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(formData),
    });

    const result = await this.handleResponse<{ success: boolean; message: string; submissionId?: string }>(response);
    console.log('Formulář odeslán:', result);
    
    return result;
  }

  async searchCompanies(query: string): Promise<RaynetCompany[]> {
    this.initialize();

    if (!query || query.trim().length < 2) {
      return [];
    }

    console.log('Vyhledávání firem:', query);

    try {
      const response = await fetch(`${this.baseUrl}/raynet/companies/search?query=${encodeURIComponent(query.trim())}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      const result = await this.handleResponse<{ success: boolean; data: RaynetCompany[] }>(response);
      
      if (result.success) {
        console.log('Nalezeno firem:', result.data.length);
        return result.data;
      }
      
      return [];
    } catch (error) {
      console.error('Chyba při vyhledávání firem:', error);
      return [];
    }
  }

  async getCompanyDetail(companyId: number): Promise<RaynetCompany> {
    this.initialize();

    console.log('Načítání detailu firmy:', companyId);

    try {
      const response = await fetch(`${this.baseUrl}/raynet/companies/${companyId}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      const result = await this.handleResponse<{ success: boolean; data: RaynetCompany }>(response);
      
      if (result.success) {
        console.log('Detail firmy načten, počet adres:', result.data.addresses?.length || 0);
        return result.data;
      }
      
      throw new Error('Nepodařilo se načíst detail firmy');
    } catch (error) {
      console.error('Chyba při načítání detailu firmy:', error);
      throw error;
    }
  }

  async getCompanyContactPersons(companyId: number, query?: string): Promise<RaynetContactPerson[]> {
    this.initialize();

    console.log('Načítání kontaktních osob pro firmu:', companyId, query ? `s dotazem: ${query}` : '');

    try {
      const searchParams = new URLSearchParams();
      if (query && query.trim().length > 0) {
        searchParams.append('query', query.trim());
      }

      const url = `${this.baseUrl}/raynet/companies/${companyId}/persons${searchParams.toString() ? '?' + searchParams.toString() : ''}`;
      
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      const result = await this.handleResponse<{ success: boolean; data: RaynetContactPerson[] }>(response);
      
      if (result.success) {
        console.log('Nalezeno kontaktních osob:', result.data.length);
        return result.data;
      }
      
      return [];
    } catch (error) {
      console.error('Chyba při načítání kontaktních osob:', error);
      return [];
    }
  }

  // ARES methods
  async searchAresCompanies(query: string): Promise<AresCompany[]> {
    this.initialize();

    if (!query || query.trim().length < 2) {
      return [];
    }

    console.log('Vyhledávání firem v ARES:', query);

    try {
      const response = await fetch(`${this.baseUrl}/ares/companies/search?query=${encodeURIComponent(query.trim())}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      const result = await this.handleResponse<{ success: boolean; data: AresCompany[] }>(response);
      
      if (result.success) {
        console.log('Nalezeno firem v ARES:', result.data.length);
        return result.data;
      }
      
      return [];
    } catch (error) {
      console.error('Chyba při vyhledávání v ARES:', error);
      return [];
    }
  }

  async getAresCompanyDetail(ico: string): Promise<AresCompany> {
    this.initialize();

    console.log('Načítání detailu firmy z ARES:', ico);

    try {
      const response = await fetch(`${this.baseUrl}/ares/companies/${ico}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      const result = await this.handleResponse<{ success: boolean; data: AresCompany }>(response);
      
      if (result.success) {
        console.log('Detail firmy z ARES načten');
        return result.data;
      }
      
      throw new Error('Nepodařilo se načíst detail firmy z ARES');
    } catch (error) {
      console.error('Chyba při načítání detailu firmy z ARES:', error);
      throw error;
    }
  }

  // Combined search method (Raynet first, then ARES)
  async searchCompaniesCombined(query: string): Promise<CompanySearchResult[]> {
    const results: CompanySearchResult[] = [];

    try {
      // First search in Raynet
      const raynetCompanies = await this.searchCompanies(query);
      
      // Transform Raynet results
      const raynetResults: CompanySearchResult[] = raynetCompanies.map(company => ({
        ...company,
        source: 'RAYNET' as const
      }));
      
      results.push(...raynetResults);

      // If no results in Raynet or less than 3, search in ARES
      if (raynetResults.length < 3) {
        console.log('Doplňuji výsledky z ARES...');
        const aresCompanies = await this.searchAresCompanies(query);
        
        // Transform ARES results and filter out duplicates by ICO
        const existingIcos = new Set(raynetResults.map(r => r.ico).filter(Boolean));
        
        const aresResults: CompanySearchResult[] = aresCompanies
          .filter(company => !existingIcos.has(company.ico))
          .map(company => ({
            ...company,
            source: 'ARES' as const
          }));
        
        results.push(...aresResults);
      }

      console.log(`Celkem nalezeno: ${results.length} firem (${raynetResults.length} Raynet, ${results.length - raynetResults.length} ARES)`);
      
      return results;
    } catch (error) {
      console.error('Chyba při kombinovaném vyhledávání:', error);
      // Return at least Raynet results if available
      return results;
    }
  }

  async getRaynetUsers(): Promise<RaynetUser[]> {
    this.initialize();

    console.log('Načítání uživatelů Raynet');

    try {
      const response = await fetch(`${this.baseUrl}/raynet/users`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      const result = await this.handleResponse<{ success: boolean; data: RaynetUser[] }>(response);
      
      if (result.success) {
        console.log('Nalezeno uživatelů:', result.data.length);
        return result.data;
      }
      
      return [];
    } catch (error) {
      console.error('Chyba při načítání uživatelů:', error);
      return [];
    }
  }

  isConfigured(): boolean {
    // Backend handles configuration, so we assume it's always configured
    return true;
  }

  // Nové metody pro vytváření entit v Raynetu

  async createCompany(companyData: RaynetCompanyCreateRequest): Promise<RaynetCompanyCreateResponse> {
    this.initialize();

    console.log('Vytváření firmy v Raynetu:', companyData);

    const response = await fetch(`${this.baseUrl}/raynet/companies`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(companyData),
    });

    const result = await this.handleResponse<RaynetCompanyCreateResponse>(response);
    console.log('Firma vytvořena:', result);
    
    return result;
  }

  async addCompanyAddress(companyId: number, addressData: RaynetAddressCreateRequest): Promise<RaynetAddressCreateResponse> {
    this.initialize();

    console.log('Přidávání adresy k firmě:', companyId, addressData);

    const response = await fetch(`${this.baseUrl}/raynet/companies/${companyId}/addresses`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(addressData),
    });

    const result = await this.handleResponse<RaynetAddressCreateResponse>(response);
    console.log('Adresa přidána:', result);
    
    return result;
  }

  async createContactPerson(contactPersonData: RaynetContactPersonCreateRequest): Promise<RaynetContactPersonCreateResponse> {
    this.initialize();

    console.log('Vytváření kontaktní osoby v Raynetu:', contactPersonData);

    const response = await fetch(`${this.baseUrl}/raynet/persons`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(contactPersonData),
    });

    const result = await this.handleResponse<RaynetContactPersonCreateResponse>(response);
    console.log('Kontaktní osoba vytvořena:', result);
    
    return result;
  }

  async createMeeting(meetingData: RaynetMeetingCreateRequest): Promise<RaynetMeetingCreateResponse> {
    this.initialize();

    console.log('Vytváření schůzky v Raynetu:', meetingData);

    const response = await fetch(`${this.baseUrl}/raynet/meetings`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(meetingData),
    });

    const result = await this.handleResponse<RaynetMeetingCreateResponse>(response);
    console.log('Schůzka vytvořena:', result);
    
    return result;
  }

  // Hlavní metoda pro odeslání dotazníku
  async submitSurvey(surveyRequest: SurveySubmissionRequest): Promise<SurveySubmissionResponse> {
    this.initialize();

    console.log('Odesílání dotazníku do Raynetu:', surveyRequest);

    const response = await fetch(`${this.baseUrl}/raynet/surveys/submit`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(surveyRequest),
    });

    const result = await this.handleResponse<SurveySubmissionResponse>(response);
    console.log('Dotazník odeslán:', result);
    
    return result;
  }
}

// Form persistence API
export const formApi = {
  // Save new form
  saveForm: async (formData: {
    companyName: string;
    contactPerson?: string;
    email?: string;
    phone?: string;
    visitDate?: string;
    representative?: string;
    stepData: any;
    status?: string;
  }) => {
    console.log('💾 Saving form to database:', formData.companyName);
    
    const baseUrl = import.meta.env.VITE_API_BASE_URL || 'http://localhost:3002/api';
    const response = await fetch(`${baseUrl}/forms`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(formData),
    });

    const result = await response.json();
    if (!response.ok) {
      throw new Error(result.error || 'Chyba při ukládání formuláře');
    }

    return result;
  },

  // Update existing form
  updateForm: async (uuid: string, updateData: {
    stepData?: any;
    finalSubmission?: any;
    status?: string;
    raynetPersonId?: number;
    raynetLeadId?: number;
  }) => {
    console.log('🔄 Updating form in database:', uuid);
    
    const baseUrl = import.meta.env.VITE_API_BASE_URL || 'http://localhost:3002/api';
    const response = await fetch(`${baseUrl}/forms/${uuid}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(updateData),
    });

    const result = await response.json();
    if (!response.ok) {
      throw new Error(result.error || 'Chyba při aktualizaci formuláře');
    }

    return result;
  },

  // Load form by UUID
  loadForm: async (uuid: string) => {
    console.log('🔍 Loading form from database:', uuid);
    
    const baseUrl = import.meta.env.VITE_API_BASE_URL || 'http://localhost:3002/api';
    const response = await fetch(`${baseUrl}/forms/${uuid}`);
    const result = await response.json();
    
    if (!response.ok) {
      throw new Error(result.error || 'Chyba při načítání formuláře');
    }

    return result.data;
  },

  // List forms
  listForms: async (filters?: {
    representative?: string;
    status?: string;
    limit?: number;
  }) => {
    console.log('📋 formApi.listForms: Volání API s filtry:', filters);
    
    const baseUrl = import.meta.env.VITE_API_BASE_URL || 'http://localhost:3002/api';
    const params = new URLSearchParams();
    if (filters?.representative) params.append('representative', filters.representative);
    if (filters?.status) params.append('status', filters.status);
    if (filters?.limit) params.append('limit', filters.limit.toString());

    const finalUrl = `${baseUrl}/forms?${params.toString()}`;
    console.log('📋 formApi.listForms: Konečná URL:', finalUrl);
    console.log('📋 formApi.listForms: Parametry:', Object.fromEntries(params));

    const response = await fetch(finalUrl);
    const result = await response.json();
    
    if (!response.ok) {
      console.error('❌ formApi.listForms: API error:', result);
      throw new Error(result.error || 'Chyba při načítání formulářů');
    }

    console.log('✅ formApi.listForms: API response:', {
      success: result.success,
      count: result.count,
      dataLength: result.data?.length
    });
    
    if (result.data) {
      result.data.forEach((form: any, index: number) => {
        console.log(`📋 formApi.listForms: Response form ${index + 1}:`, {
          uuid: form.uuid?.slice(0, 8),
          company: form.company_name,
          representative: form.representative,
          status: form.status
        });
      });
    }

    return result.data;
  },

  // Delete form
  deleteForm: async (uuid: string) => {
    console.log('🗑️ Deleting form:', uuid);
    
    const baseUrl = import.meta.env.VITE_API_BASE_URL || 'http://localhost:3002/api';
    const response = await fetch(`${baseUrl}/forms/${uuid}`, {
      method: 'DELETE',
    });

    const result = await response.json();
    if (!response.ok) {
      throw new Error(result.error || 'Chyba při mazání formuláře');
    }

    return result;
  },
};

// Admin API for form management
export const adminFormApi = {
  // Get all forms with advanced filtering and pagination
  getForms: async (filters?: {
    page?: number;
    limit?: number;
    status?: string;
    representative?: string;
    company_name?: string;
    date_from?: string;
    date_to?: string;
    sort_by?: string;
    sort_order?: 'asc' | 'desc';
  }) => {
    console.log('🔧 Admin loading forms:', filters);
    
    const baseUrl = import.meta.env.VITE_API_BASE_URL || 'http://localhost:3002/api';
    const params = new URLSearchParams();
    
    if (filters?.page) params.append('page', filters.page.toString());
    if (filters?.limit) params.append('limit', filters.limit.toString());
    if (filters?.status) params.append('status', filters.status);
    if (filters?.representative) params.append('representative', filters.representative);
    if (filters?.company_name) params.append('company_name', filters.company_name);
    if (filters?.date_from) params.append('date_from', filters.date_from);
    if (filters?.date_to) params.append('date_to', filters.date_to);
    if (filters?.sort_by) params.append('sort_by', filters.sort_by);
    if (filters?.sort_order) params.append('sort_order', filters.sort_order);

    const response = await fetch(`${baseUrl}/admin/forms?${params.toString()}`);
    const result = await response.json();
    
    if (!response.ok) {
      throw new Error(result.error || 'Chyba při načítání admin formulářů');
    }

    return result;
  },

  // Get form statistics
  getStats: async () => {
    console.log('📊 Loading admin form statistics');
    
    const baseUrl = import.meta.env.VITE_API_BASE_URL || 'http://localhost:3002/api';
    const response = await fetch(`${baseUrl}/admin/forms/stats`);
    const result = await response.json();
    
    if (!response.ok) {
      throw new Error(result.error || 'Chyba při načítání admin statistik');
    }

    return result;
  },

  // Bulk delete forms
  bulkDeleteForms: async (uuids: string[]) => {
    console.log('🗑️ Bulk deleting forms:', uuids.length);
    
    const baseUrl = import.meta.env.VITE_API_BASE_URL || 'http://localhost:3002/api';
    const response = await fetch(`${baseUrl}/admin/forms/bulk`, {
      method: 'DELETE',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ uuids }),
    });

    const result = await response.json();
    if (!response.ok) {
      throw new Error(result.error || 'Chyba při hromadném mazání formulářů');
    }

    return result;
  },
};

// Statistics API
export const statisticsApi = {
  // Get overview statistics
  getOverview: async () => {
    const baseUrl = import.meta.env.VITE_API_BASE_URL || 'http://localhost:3002/api';
    const response = await fetch(`${baseUrl}/admin/statistics/overview`);
    const result = await response.json();
    
    if (!response.ok) {
      throw new Error(result.message || 'Chyba při načítání statistik');
    }
    
    return result.data;
  },

  // Get detailed statistics by category
  getDetailedStats: async (category: string, filters?: {
    startDate?: string;
    endDate?: string;
    representative?: string;
    status?: string;
  }) => {
    const baseUrl = import.meta.env.VITE_API_BASE_URL || 'http://localhost:3002/api';
    const params = new URLSearchParams();
    
    if (filters?.startDate) params.append('startDate', filters.startDate);
    if (filters?.endDate) params.append('endDate', filters.endDate);
    if (filters?.representative) params.append('representative', filters.representative);
    if (filters?.status) params.append('status', filters.status);
    
    const response = await fetch(`${baseUrl}/admin/statistics/detailed/${category}?${params}`);
    const result = await response.json();
    
    if (!response.ok) {
      throw new Error(result.message || 'Chyba při načítání detailních statistik');
    }
    
    return result.data;
  },

  // Get submissions with pagination and filtering
  getSubmissions: async (params?: {
    page?: number;
    limit?: number;
    search?: string;
    representative?: string;
    status?: string;
    startDate?: string;
    endDate?: string;
  }) => {
    const baseUrl = import.meta.env.VITE_API_BASE_URL || 'http://localhost:3002/api';
    const queryParams = new URLSearchParams();
    
    if (params?.page) queryParams.append('page', params.page.toString());
    if (params?.limit) queryParams.append('limit', params.limit.toString());
    if (params?.search) queryParams.append('search', params.search);
    if (params?.representative) queryParams.append('representative', params.representative);
    if (params?.status) queryParams.append('status', params.status);
    if (params?.startDate) queryParams.append('startDate', params.startDate);
    if (params?.endDate) queryParams.append('endDate', params.endDate);
    
    const response = await fetch(`${baseUrl}/admin/statistics/submissions?${queryParams}`);
    const result = await response.json();
    
    if (!response.ok) {
      throw new Error(result.message || 'Chyba při načítání formulářů');
    }
    
    return result.data;
  },

  // Export statistics
  exportData: async (format: 'csv' | 'json', filters?: {
    startDate?: string;
    endDate?: string;
    representative?: string;
    status?: string;
  }) => {
    const baseUrl = import.meta.env.VITE_API_BASE_URL || 'http://localhost:3002/api';
    const params = new URLSearchParams();
    
    if (filters?.startDate) params.append('startDate', filters.startDate);
    if (filters?.endDate) params.append('endDate', filters.endDate);
    if (filters?.representative) params.append('representative', filters.representative);
    if (filters?.status) params.append('status', filters.status);
    
    const response = await fetch(`${baseUrl}/admin/statistics/export/${format}?${params}`);
    
    if (!response.ok) {
      const result = await response.json();
      throw new Error(result.message || 'Chyba při exportu dat');
    }
    
    if (format === 'csv') {
      return await response.text();
    } else {
      return await response.json();
    }
  },

  // Get drill-down data for specific statistic
  getDrillDownData: async (
    type: 'status' | 'representative' | 'monthly' | 'design' | 'cooperation' | 'competition' | 'regional',
    value: string | number,
    filters?: Record<string, any>
  ) => {
    const baseUrl = import.meta.env.VITE_API_BASE_URL || 'http://localhost:3002/api';
    const params = new URLSearchParams();
    
    // Add base parameters
    params.append('type', type);
    params.append('value', String(value));
    
    // Add any additional filters
    if (filters) {
      Object.entries(filters).forEach(([key, val]) => {
        if (val) params.append(key, String(val));
      });
    }
    
    const response = await fetch(`${baseUrl}/admin/statistics/drill-down?${params}`);
    const result = await response.json();
    
    if (!response.ok) {
      throw new Error(result.message || 'Chyba při načítání drill-down dat');
    }
    
    return result.data;
  },
};

// Singleton instance pro lazy initialization
export const raynetClient = new RaynetClient(); 